import { OrderProductsAPI } from '@business/api/order';
import { PageQuery } from '@business/entities/common';
import { OrderBrandId, OrderProductId, OrderProductResponse } from '@business/entities/order';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showConfirmation, showDeleteConfirmation } from '@modules/common';
import { ProductFormData } from '@modules/orders/ProductsPage/AddEditProductOverlay/AddEditProductOverlay';
import { switchMap } from 'rxjs/operators';

export class OrderProductBloc extends BaseBloc {
  private readonly productsRepo = new Repository<OrderProductResponse>({
    getItemId: product => product.id,
  });

  readonly products$ = this.productsRepo.items$;
  readonly selectProduct = this.productsRepo.selectItem;

  onReset() {
    this.productsRepo.reset();
  }

  fetchProductsPaginated = (query: PageQuery) => {
    return OrderProductsAPI.fetchProductsPaginated(query);
  };

  searchProducts = (
    search: string,
    products?: OrderProductId[],
    vendorId?: string,
    categoryId?: string,
    brandId?: OrderBrandId,
    isActive = false,
    fromAddProduct = false,
    page = 1,
    requestId?: number,
  ) => {
    const status = isActive ? 'active' : '';
    return OrderProductsAPI.fetchProductsPaginated({ search, page, requestId }, products, vendorId, categoryId, brandId, status, fromAddProduct).pipe(
      handleMessage({ error: requestMessage('fetch_order_products_error') }),
    );
  };

  fetchProducts = (isPaginated?: string) => {
    return OrderProductsAPI.fetchProducts(isPaginated).pipe(
      this.productsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_order_products_error') }),
    );
  };

  fetchAllProducts = () => {
    return OrderProductsAPI.fetchAllProducts().pipe(
      this.productsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_order_products_error') }),
    );
  };

  fetchProduct = (id: OrderProductId) => {
    return OrderProductsAPI.fetchProduct(id).pipe(
      this.productsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_order_products_error') }),
    );
  };

  syncProducts = () => {
    return OrderProductsAPI.syncProducts().pipe(
      this.productsRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('sync_order_product'),
      }),
    );
  };

  createProduct = (data: Partial<ProductFormData>) => {
    return OrderProductsAPI.createProduct(data).pipe(
      this.productsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_order_product'),
      }),
    );
  };

  updateProduct = (id: OrderProductId, data: Partial<ProductFormData>) => {
    return OrderProductsAPI.updateProduct(id, data).pipe(
      this.productsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_product'),
      }),
    );
  };

  deleteProduct = (id: OrderProductId) => {
    return showDeleteConfirmation('Delete Product', `Do you really want to remove this product?`).pipe(
      switchMap(() => OrderProductsAPI.deleteProduct(id)),
      this.productsRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_order_product'),
      }),
    );
  };

  toggleStatus = (id: OrderProductId) => {
    return showConfirmation('Product Status', `Do you really want to change the status of this product?`).pipe(
      switchMap(() => OrderProductsAPI.toggleStatus(id)),
      this.productsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_product'),
      }),
    );
  };
}
